import { ArrowUpRight, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type CSSProperties,
  type ReactNode,
} from "react";
import { detectLocale, dictionaries, type Dict, type Locale } from "./i18n";

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type I18nValue = { locale: Locale; setLocale: (value: Locale) => void; t: Dict };
const I18n = createContext<I18nValue | null>(null);
const useI18n = () => useContext(I18n)!;

function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>("en");
  useEffect(() => setLocale(detectLocale()), []);
  useEffect(() => {
    localStorage.setItem("unlocked-lang", locale);
    document.documentElement.lang = locale;
    document.title = dictionaries[locale].meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", dictionaries[locale].meta.description);
  }, [locale]);
  return <I18n.Provider value={{ locale, setLocale, t: dictionaries[locale] }}>{children}</I18n.Provider>;
}

type SiteValue = { open: boolean; setOpen: () => void; burst: number; triggerBurst: () => void };
const Site = createContext<SiteValue | null>(null);
const useSite = () => useContext(Site)!;

function SiteProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [burst, setBurst] = useState(0);
  const setOpen = useCallback(() => setOpenState(true), []);
  const triggerBurst = useCallback(() => { setOpenState(true); setBurst((n) => n + 1); }, []);
  useEffect(() => {
    let keys = "";
    const listener = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return;
      keys = (keys + event.key.toLowerCase()).slice(-6);
      if (keys === "unlock") { keys = ""; triggerBurst(); }
    };
    addEventListener("keydown", listener);
    return () => removeEventListener("keydown", listener);
  }, [triggerBurst]);
  return <Site.Provider value={{ open, setOpen, burst, triggerBurst }}>{children}</Site.Provider>;
}

function LockMark({ size = 22, open = false, className = "" }: { size?: number; open?: boolean; className?: string }) {
  return (
    <svg className={`lock-mark ${open ? "is-open" : ""} ${className}`} width={size} height={Math.round(size * 1.125)} viewBox="0 0 32 36" fill="none" aria-hidden="true">
      <g className="shackle" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 17.5V11.2C9 7.35 12.05 4.4 16 4.4s7 2.95 7 6.8V17.5" /></g>
      <rect x="6" y="17.2" width="20" height="15.2" rx="3.2" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="16" cy="24.2" r="1.55" fill="currentColor" /><path d="M16 25.6v3.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Reveal({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reducedMotion()) { el.classList.add("is-in"); return; }
    const io = new IntersectionObserver(([entry]) => { if (entry?.isIntersecting) { el.classList.add("is-in"); io.disconnect(); } }, { threshold: 0.14, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`reveal ${className}`}>{children}</div>;
}

function Effects() {
  const cursor = useRef<HTMLDivElement>(null);
  const { burst } = useSite();
  const [pieces, setPieces] = useState<Array<{ x: number; y: number; r: number }>>([]);
  useEffect(() => {
    if (!burst) return;
    setPieces(Array.from({ length: 28 }, () => ({ x: (Math.random() - .5) * 230, y: 70 + Math.random() * 160, r: (Math.random() - .5) * 500 })));
    const id = setTimeout(() => setPieces([]), 1200);
    return () => clearTimeout(id);
  }, [burst]);
  useEffect(() => {
    if (reducedMotion() || !matchMedia("(pointer: fine)").matches) return;
    let raf = 0, x = -30, y = -30, tx = -30, ty = -30;
    document.documentElement.classList.add("custom-cursor-on");
    const move = (event: PointerEvent) => {
      tx = event.clientX; ty = event.clientY;
      const target = event.target as HTMLElement;
      cursor.current?.classList.toggle("is-hot", !!target.closest("a,button"));
      cursor.current?.classList.toggle("is-hidden", !!target.closest("input,textarea,select"));
    };
    const tick = () => { x += (tx - x) * .28; y += (ty - y) * .28; if (cursor.current) cursor.current.style.transform = `translate3d(${x}px,${y}px,0)`; raf = requestAnimationFrame(tick); };
    addEventListener("pointermove", move, { passive: true }); tick();
    return () => { removeEventListener("pointermove", move); cancelAnimationFrame(raf); document.documentElement.classList.remove("custom-cursor-on"); };
  }, []);
  return <><div className="grain" aria-hidden="true" /><div ref={cursor} className="cursor" aria-hidden="true" /><div className="page-loader" aria-hidden="true"><div><LockMark size={56} className="intro-lock" /><span>UNLOCKED</span></div></div>{pieces.map((p, i) => <i key={`${burst}-${i}`} className="confetti" style={{ "--dx": `${p.x}px`, "--dy": `${p.y}px`, "--rot": `${p.r}deg`, "--delay": `${i * 8}ms` } as CSSProperties} />)}</>;
}

function LanguageSwitch() {
  const { locale, setLocale, t } = useI18n();
  return <div className="lang-switch" role="group" aria-label={t.a11y.language}>{(["en", "ru", "lv"] as Locale[]).map((id) => <button key={id} className="lang-btn" aria-pressed={locale === id} onClick={() => setLocale(id)}>{id.toUpperCase()}</button>)}</div>;
}

function Nav() {
  const { t } = useI18n();
  const { open: lockOpen, setOpen: unlock, triggerBurst } = useSite();
  const [compact, setCompact] = useState(false);
  const [menu, setMenu] = useState(false);
  const clicks = useRef(0);
  useEffect(() => { const scroll = () => setCompact(scrollY > 18); scroll(); addEventListener("scroll", scroll, { passive: true }); return () => removeEventListener("scroll", scroll); }, []);
  useEffect(() => { document.body.style.overflow = menu ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [menu]);
  const links = [["services", t.nav.services], ["about", t.nav.about], ["contact", t.nav.contact]];
  const brandClick = () => { unlock(); clicks.current += 1; if (clicks.current >= 3) { clicks.current = 0; triggerBurst(); } };
  return <header className={`site-nav ${compact ? "is-compact" : ""}`}><div className="shell nav-shell"><a className="brand" href="#top" aria-label={t.a11y.home} onClick={brandClick}><LockMark open={lockOpen} /><span>UNLOCKED</span></a><nav className="nav-links">{links.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}</nav><div className="nav-right"><a className="button nav-cta" href="#contact">{t.nav.cta}</a><LanguageSwitch /><button className="hamburger" aria-label={t.a11y.menu} aria-expanded={menu} onClick={() => setMenu(true)}><span /><span /><span /></button></div></div>{menu && <div className="drawer-wrap" onMouseDown={() => setMenu(false)}><aside className="drawer" onMouseDown={(e) => e.stopPropagation()}><button className="drawer-close" aria-label={t.a11y.close} onClick={() => setMenu(false)}><X size={18} /></button>{links.map(([id, label]) => <a key={id} href={`#${id}`} onClick={() => setMenu(false)}>{label}</a>)}<a href="#contact" onClick={() => setMenu(false)}>{t.nav.cta}</a><LanguageSwitch /></aside></div>}</header>;
}

function Typewriter({ words }: { words: string[] }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (reducedMotion()) return;
    let index = 0, length = words[0].length, deleting = true, timer = 0;
    if (ref.current) ref.current.textContent = words[0];
    const tick = () => {
      const word = words[index];
      length += deleting ? -1 : 1;
      if (ref.current) ref.current.textContent = word.slice(0, Math.max(0, length));
      let delay = deleting ? 32 : 54;
      if (length <= 0) { deleting = false; index = (index + 1) % words.length; delay = 90; }
      else if (!deleting && length >= words[index].length) { deleting = true; delay = 820; }
      timer = window.setTimeout(tick, delay);
    };
    timer = window.setTimeout(tick, 1500);
    return () => clearTimeout(timer);
  }, [words]);
  return <span className="hero-type" ref={ref}>{words[0]}</span>;
}

function Hero() {
  const { t, locale } = useI18n();
  const grid = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (reducedMotion()) return;
    const move = (e: PointerEvent) => { if (grid.current) grid.current.style.transform = `translate3d(${(e.clientX / innerWidth - .5) * 14}px,${(e.clientY / innerHeight - .5) * 10}px,0)`; };
    addEventListener("pointermove", move, { passive: true }); return () => removeEventListener("pointermove", move);
  }, []);
  return <section id="top" className="hero"><div ref={grid} className="hero-grid" /><div className="scanline" /><div className="particles">{Array.from({ length: 8 }, (_, i) => <span key={i} />)}</div><div className="hero-silhouette"><LockMark size={280} open /></div><div className="shell hero-copy"><p className="eyebrow">{t.hero.eyebrow}</p><h1 aria-label={t.hero.aria}>{t.hero.prefix} <Typewriter key={locale} words={t.hero.words} /><span className="hero-caret" /><em>.</em></h1><p className="hero-sub">{t.hero.sub}</p></div><div className="shell scroll-hint"><i />{t.hero.scroll}</div></section>;
}

function Services() {
  const { t } = useI18n();
  const [extra, setExtra] = useState<keyof typeof t.services.extra>("design");
  const cards = Object.entries(t.services.cards);
  return <section id="services" className="section"><div className="shell"><Reveal><p className="section-kicker">{t.services.kicker}</p><h2 className="section-title">{t.services.title}</h2><p className="section-lead">{t.services.lead}</p></Reveal><div className="bento">{cards.map(([id, card], index) => <article key={id} className={`bento-card card-${id}`}><h3>{card.title}</h3><p>{card.copy}</p><div className="service-scope"><span>{card.scope}</span>{index === 0 && <a className="metric-go" href="#contact" aria-label={t.nav.contact}><ArrowUpRight size={18} /></a>}</div></article>)}</div><Reveal className="more-services"><p className="more-label">{t.services.more}</p><div className="chip-row" role="tablist">{(Object.keys(t.services.extra) as Array<keyof typeof t.services.extra>).map((id) => <button key={id} className="chip" role="tab" aria-selected={extra === id} onClick={() => setExtra(id)}>{t.services.extra[id][0]}</button>)}</div><p className="chip-detail">{t.services.extra[extra][1]}</p></Reveal></div></section>;
}

function Takeover() {
  const { t } = useI18n();
  return <section className="section takeover-section"><div className="shell"><Reveal><p className="section-kicker">{t.takeover.kicker}</p><h2 className="section-title">{t.takeover.title}</h2><p className="section-lead">{t.takeover.lead}</p></Reveal><div className="takeover-grid">{t.takeover.items.map(([title, copy], index) => <Reveal key={title} className="takeover-card"><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></Reveal>)}</div></div></section>;
}

function Flow() {
  const { t } = useI18n();
  return <section className="section flow-section"><div className="shell"><Reveal><p className="section-kicker">{t.flow.kicker}</p><h2 className="section-title">{t.flow.title}</h2><p className="section-lead">{t.flow.lead}</p></Reveal><Reveal className="system-flow">{t.flow.steps.map(([number, title, copy]) => <article key={number}><span>{number}</span><h3>{title}</h3><p>{copy}</p></article>)}</Reveal></div></section>;
}

function Process() {
  const { t } = useI18n();
  return <section id="about" className="section"><div className="shell"><Reveal><p className="section-kicker">{t.process.kicker}</p><h2 className="section-title">{t.process.title}</h2><p className="section-lead">{t.process.lead}</p><div className="process"><i />{t.process.steps.map(([number, title, copy]) => <article key={number}><span>{number}</span><h3>{title}</h3><p>{copy}</p></article>)}</div></Reveal></div></section>;
}

function Cta() {
  const { t } = useI18n();
  const { setOpen } = useSite();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => { const el = ref.current; if (!el) return; const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) setOpen(); }, { threshold: .45 }); io.observe(el); return () => io.disconnect(); }, [setOpen]);
  return <section ref={ref} className="cta-band"><div className="shell"><Reveal><h2>{t.cta.title}</h2><p>{t.cta.sub}</p><a className="button" href="#contact">{t.cta.button}</a></Reveal></div></section>;
}

function Contact() {
  const { t } = useI18n();
  const { setOpen } = useSite();
  const [status, setStatus] = useState<"idle" | "sending" | "success">("idle");
  const [error, setError] = useState("");
  const endpoint = String(import.meta.env.VITE_FORMSPREE_ENDPOINT || "https://formspree.io/f/xppqnzyn");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setError("");
    const form = event.currentTarget;
    if (!form.reportValidity()) { setError(t.contact.required); return; }
    if (!/^https:\/\/formspree\.io\/f\/[A-Za-z0-9_-]+$/.test(endpoint)) { setError(t.contact.config); return; }
    setStatus("sending");
    try {
      const response = await fetch(endpoint, { method: "POST", headers: { Accept: "application/json" }, body: new FormData(form) });
      if (!response.ok) throw new Error();
      setStatus("success"); setOpen(); form.reset();
    } catch { setStatus("idle"); setError(t.contact.error); }
  };
  return <section id="contact" className="section"><div className="shell"><Reveal><p className="section-kicker">{t.contact.kicker}</p><h2 className="section-title">{t.contact.title}</h2><p className="section-lead">{t.contact.lead}</p></Reveal>{status === "success" ? <div className="form-success"><LockMark size={48} open /><h3>{t.contact.successTitle}</h3><p>{t.contact.successBody}</p></div> : <form className="form-grid" onSubmit={submit}><label>{t.contact.name}<input name="name" autoComplete="name" required /></label><label>{t.contact.email}<input name="email" type="email" autoComplete="email" required /></label><label className="full">{t.contact.service}<select name="service" defaultValue="" required><option value="" disabled>{t.contact.select}</option>{t.contact.services.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label className="full">{t.contact.message}<textarea name="message" required /></label><div className="full form-actions"><button className="button" disabled={status === "sending"}>{status === "sending" ? t.contact.sending : t.contact.send}</button><p className="form-privacy">{t.contact.privacy}</p>{error && <p className="form-error" role="alert">{error}</p>}</div></form>}</div></section>;
}

function Footer() {
  const { t } = useI18n();
  const serviceLinks = [t.services.cards.marketing.title, t.services.cards.custom.title, t.services.cards.automation.title, t.services.cards.crm.title, t.footer.designSeo, t.footer.website];
  return <footer className="site-footer"><div className="shell"><div className="footer-grid"><div><a className="brand" href="#top"><LockMark size={20} /><span>UNLOCKED</span></a><p>{t.footer.blurb}</p></div><div><h4>{t.footer.services}</h4>{serviceLinks.map((label) => <a key={label} href="#services">{label}</a>)}</div><div><h4>{t.footer.company}</h4><a href="#about">{t.nav.about}</a><a href="#contact">{t.nav.contact}</a><a href="#contact">{t.nav.cta}</a></div></div><p className="footer-copy">{t.footer.copy}</p></div></footer>;
}

function Home() {
  const { t } = useI18n();
  return <SiteProvider><a className="skip-link" href="#main">{t.a11y.skip}</a><Effects /><Nav /><main id="main"><Hero /><Services /><Takeover /><Flow /><Process /><Cta /><Contact /></main><Footer /></SiteProvider>;
}

export default function App() {
  return <I18nProvider><Home /></I18nProvider>;
}
